import ForgotPassword from "components/auth/ForgotPassword";
import PasswordLogin from "components/auth/PasswordLogin";
import PasswordlessLogin from "components/auth/PasswordlessLogin";
import { AnimatePresence, motion } from "framer-motion";
import { useToast } from "hooks/use-toast";
import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { logDev } from "services/CommonService";
import { toast as alert } from "sonner";
import { useRemoteService } from "../services/authService";
import {
  loginFailure,
  loginStart,
  loginSuccess,
} from "../store/slices/authSlice";

type AuthMethod = "password" | "passwordless" | "forgot-password";

const Login = () => {
  const [authMethod, setAuthMethod] = useState<AuthMethod>("password");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { toast } = useToast();
  const { sendPostToServerWithOutToken } = useRemoteService();

  const handlePasswordLogin = async (email: string, password: string) => {
    dispatch(loginStart());

    try {
      const res = await sendPostToServerWithOutToken(
        "auth/login/web",
        JSON.stringify({
          username: email,
          password,
        }),
      );

      const { returnCode, returnObject, returnMessage } = res;
      const user = returnObject.user;

      if (returnCode === 200) {
        // Clear session before resetting it
        localStorage.removeItem("loggedInUser");

        localStorage.setItem("loggedInUser", JSON.stringify(returnObject));
        dispatch(
          loginSuccess({
            user,
            token: returnObject.token,
            expiry: returnObject.expiresIn,
          }),
        );
        toast({
          title: "Login Successful!",
          description: `Welcome back, ${user.username}!`,
        });
        navigate("/", { replace: true });
      } else {
        alert.error("Authentication Failure!", {
          description: returnMessage,
        });
        throw new Error("Invalid credentials");
      }
    } catch (error) {
      toast({
        title: "Login failed",
        description: "Invalid email or password",
        variant: "destructive",
      });
      dispatch(loginFailure("Invalid credentials"));
      throw error;
    }
  };

  const handleSendVerificationCode = async (email: string) => {
    try {
      const res = await sendPostToServerWithOutToken(
        "auth/send-verification",
        JSON.stringify({ email }),
      );

      if (res.returnCode !== 200) {
        throw new Error("Failed to send verification code");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleVerifyCode = async (email: string, code: string) => {
    logDev(email, code);
    try {
      const res = await sendPostToServerWithOutToken(
        "auth/verify-token",
        JSON.stringify({
          email,
          token: code,
        }),
      );

      const { returnCode, returnObject } = res;
      const user = returnObject.user;

      if (returnCode === 200) {
        localStorage.setItem("loggedInUser", JSON.stringify(returnObject));
        dispatch(
          loginSuccess({
            user,
            token: returnObject.token,
            expiry: returnObject.expiresIn,
          }),
        );
        toast({
          title: "Verified successfully",
          description: `Welcome back, ${user.firstName}!`,
        });
        navigate("/");
      } else {
        throw new Error("Invalid verification code");
      }
    } catch (error) {
      throw error;
    }
  };

  const handleResetPassword = async (email: string) => {
    try {
      const res = await sendPostToServerWithOutToken(
        "auth/reset-password",
        JSON.stringify({ email }),
      );

      if (res.returnCode !== 200) {
        throw new Error("Failed to send reset instructions");
      }
    } catch (error) {
      throw error;
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-100 via-white to-secondary-100 opacity-80" />
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
      </div>
      <div className="absolute right-0 top-0 -mt-24 -mr-24 w-96 h-96 bg-primary-200 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob" />
      <div className="absolute left-0 bottom-0 -mb-24 -ml-24 w-96 h-96 bg-secondary-200 rounded-full mix-blend-multiply filter blur-2xl opacity-30 animate-blob animation-delay-2000" />

      {/* Content */}
      <div className="relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="sm:mx-auto sm:w-full sm:max-w-md"
        >
          <div className="flex justify-center">
            <div className="relative">
              <div className="absolute inset-0 bg-transparent rounded-full blur-lg transform scale-150 opacity-50" />
              {/* <Scissors className="relative h-12 w-12 text-primary-600" /> */}
              <img
                alt="Tertiary Logo"
                src={require("assets/logos/tertiary-black.png")}
                className="relative h-32 w-42"
              />
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Welcome to Vella
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Choose your sign in method
          </p>
        </motion.div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/80 backdrop-blur-lg py-8 px-4 shadow-xl ring-1 ring-gray-900/10 sm:rounded-lg sm:px-10"
          >
            {authMethod !== "forgot-password" && (
              <div className="flex space-x-4 mb-6">
                <button
                  onClick={() => setAuthMethod("password")}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md ${
                    authMethod === "password"
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Password
                </button>
                <button
                  onClick={() => setAuthMethod("passwordless")}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md ${
                    authMethod === "passwordless"
                      ? "bg-primary-100 text-primary-700"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  Passwordless
                </button>
              </div>
            )}

            <AnimatePresence mode="wait">
              {authMethod === "password" && (
                <PasswordLogin
                  key="password"
                  onLogin={handlePasswordLogin}
                  onForgotPassword={() => setAuthMethod("forgot-password")}
                />
              )}
              {authMethod === "passwordless" && (
                <PasswordlessLogin
                  key="passwordless"
                  onSendCode={handleSendVerificationCode}
                  onVerifyCode={(email, code) => handleVerifyCode(email, code)}
                />
              )}
              {authMethod === "forgot-password" && (
                <ForgotPassword
                  key="forgot-password"
                  onResetPassword={handleResetPassword}
                  onBack={() => setAuthMethod("password")}
                />
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Login;
